import { useParallax } from "@/hooks/useParallax";

const ParallaxElements = () => {
  const scrollY = useParallax();

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {/* Deep background layer - moves slowest for maximum depth */}
      <div
        className="absolute w-[800px] h-[800px] rounded-full bg-primary/3 blur-[120px]"
        style={{
          top: "5%",
          left: "-15%",
          transform: `translateY(${scrollY * 0.03}px)`,
        }}
      />
      
      {/* Secondary deep layer */}
      <div
        className="absolute w-[600px] h-[600px] rounded-full bg-accent/3 blur-[100px]"
        style={{
          bottom: "10%",
          right: "-10%",
          transform: `translateY(${scrollY * 0.05}px)`,
        }}
      />

      {/* Mid-layer gradient orb - moves slow */}
      <div
        className="absolute w-[500px] h-[500px] rounded-full bg-primary/5 blur-[80px]"
        style={{
          top: "20%",
          left: "-5%",
          transform: `translateY(${scrollY * 0.08}px)`,
        }}
      />

      {/* Accent orb - moves medium */}
      <div
        className="absolute w-[350px] h-[350px] rounded-full bg-accent/5 blur-[60px]"
        style={{
          top: "45%",
          right: "-3%",
          transform: `translateY(${scrollY * 0.12}px)`,
        }}
      />

      {/* Foreground floating elements - move faster for depth contrast */}
      
      {/* Small floating circle 1 */}
      <div
        className="absolute w-2 h-2 rounded-full bg-primary/30"
        style={{
          top: "20%",
          left: "15%",
          transform: `translateY(${scrollY * -0.2}px) rotate(${scrollY * 0.1}deg)`,
        }}
      />

      {/* Small floating circle 2 */}
      <div
        className="absolute w-3 h-3 rounded-full bg-accent/20"
        style={{
          top: "60%",
          left: "80%",
          transform: `translateY(${scrollY * -0.25}px)`,
        }}
      />

      {/* Small floating circle 3 */}
      <div
        className="absolute w-1.5 h-1.5 rounded-full bg-primary/40"
        style={{
          top: "80%",
          left: "25%",
          transform: `translateY(${scrollY * -0.3}px)`,
        }}
      />
      
      {/* Extra foreground particle */}
      <div
        className="absolute w-1 h-1 rounded-full bg-accent/50"
        style={{
          top: "35%",
          right: "12%",
          transform: `translateY(${scrollY * -0.35}px)`,
        }}
      />

      {/* Geometric shape 1 - triangle outline */}
      <div
        className="absolute w-16 h-16 border border-primary/8 rotate-45"
        style={{
          top: "30%",
          right: "22%",
          transform: `translateY(${scrollY * 0.06}px) rotate(${45 + scrollY * 0.015}deg)`,
        }}
      />

      {/* Geometric shape 2 - square outline */}
      <div
        className="absolute w-12 h-12 border border-accent/8"
        style={{
          top: "70%",
          left: "12%",
          transform: `translateY(${scrollY * -0.1}px) rotate(${scrollY * 0.02}deg)`,
        }}
      />
      
      {/* Subtle diamond shape */}
      <div
        className="absolute w-10 h-10 border border-primary/5 rotate-45"
        style={{
          top: "50%",
          left: "40%",
          transform: `translateY(${scrollY * 0.04}px) rotate(${45 + scrollY * 0.01}deg)`,
        }}
      />

      {/* Line element 1 - vertical */}
      <div
        className="absolute w-px h-24 bg-gradient-to-b from-transparent via-primary/15 to-transparent"
        style={{
          top: "25%",
          left: "30%",
          transform: `translateY(${scrollY * 0.04}px)`,
        }}
      />

      {/* Line element 2 - horizontal */}
      <div
        className="absolute w-24 h-px bg-gradient-to-r from-transparent via-accent/15 to-transparent"
        style={{
          top: "55%",
          right: "28%",
          transform: `translateX(${scrollY * -0.06}px)`,
        }}
      />
      
      {/* Diagonal line */}
      <div
        className="absolute w-16 h-px bg-gradient-to-r from-transparent via-primary/10 to-transparent rotate-45"
        style={{
          top: "75%",
          right: "40%",
          transform: `translateY(${scrollY * -0.08}px) rotate(45deg)`,
        }}
      />

      {/* Dot grid pattern - subtle */}
      <div
        className="absolute grid grid-cols-4 gap-6 opacity-15"
        style={{
          top: "45%",
          left: "6%",
          transform: `translateY(${scrollY * 0.05}px)`,
        }}
      >
        {[...Array(12)].map((_, i) => (
          <div key={i} className="w-0.5 h-0.5 rounded-full bg-primary/40" />
        ))}
      </div>

      {/* Large ring - moves very slow for deep layer effect */}
      <div
        className="absolute w-48 h-48 rounded-full border border-primary/4"
        style={{
          bottom: "15%",
          right: "12%",
          transform: `translateY(${scrollY * -0.07}px) scale(${1 + scrollY * 0.00005})`,
        }}
      />
      
      {/* Second ring - different speed */}
      <div
        className="absolute w-32 h-32 rounded-full border border-accent/4"
        style={{
          top: "65%",
          left: "8%",
          transform: `translateY(${scrollY * 0.09}px)`,
        }}
      />

      {/* Cross element */}
      <div
        className="absolute"
        style={{
          top: "18%",
          right: "32%",
          transform: `translateY(${scrollY * 0.1}px) rotate(${scrollY * 0.03}deg)`,
        }}
      >
        <div className="w-4 h-px bg-primary/15" />
        <div className="w-px h-4 bg-primary/15 -translate-x-[7px] -translate-y-2" />
      </div>
      
      {/* Second cross */}
      <div
        className="absolute"
        style={{
          bottom: "30%",
          left: "20%",
          transform: `translateY(${scrollY * -0.12}px) rotate(${scrollY * -0.02}deg)`,
        }}
      >
        <div className="w-3 h-px bg-accent/15" />
        <div className="w-px h-3 bg-accent/15 -translate-x-[5px] -translate-y-1.5" />
      </div>
    </div>
  );
};

export default ParallaxElements;
